import { useLayoutEffect, useRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "./_utils";

const textareaVariants = cva(
  "block w-full bg-bg-input text-text-primary rounded-[var(--radius-control)] border transition-colors " +
    "py-sm px-md text-base placeholder:text-text-tertiary " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus " +
    "disabled:opacity-50 disabled:pointer-events-none",
  {
    variants: {
      state: {
        default: "border-border",
        error: "border-error",
      },
    },
    defaultVariants: { state: "default" },
  }
);

type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> &
  VariantProps<typeof textareaVariants> & {
    autosize?: boolean;
    ref?: React.Ref<HTMLTextAreaElement>;
  };

/**
 * Multi-line text input. Pass `autosize` to grow with content.
 *
 * @example
 * <Textarea placeholder="Write a message…" />
 * <Textarea state="error" defaultValue="oops" />
 * <Textarea autosize placeholder="Grows as you type" />
 */
export function Textarea({
  className,
  state,
  autosize,
  onInput,
  value,
  defaultValue,
  ref: externalRef,
  ...props
}: TextareaProps) {
  const internalRef = useRef<HTMLTextAreaElement | null>(null);

  const setRef = (el: HTMLTextAreaElement | null) => {
    internalRef.current = el;
    if (typeof externalRef === "function") {
      externalRef(el);
    } else if (externalRef && typeof externalRef === "object") {
      (externalRef as React.RefObject<HTMLTextAreaElement | null>).current = el;
    }
  };

  useLayoutEffect(() => {
    if (!autosize) return;
    const el = internalRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [autosize, value, defaultValue]);

  return (
    <textarea
      ref={setRef}
      className={cn(textareaVariants({ state }), className)}
      value={value}
      defaultValue={defaultValue}
      onInput={(e) => {
        if (autosize) {
          const el = e.currentTarget;
          el.style.height = "auto";
          el.style.height = `${el.scrollHeight}px`;
        }
        onInput?.(e);
      }}
      {...props}
    />
  );
}
